import { inject } from '@angular/core';
import { ResolveFn } from '@angular/router';
import { catchError, forkJoin, map, of } from 'rxjs';

import { CloudData } from 'angular-tag-cloud-module';
import { PollService } from '../shared/services/poll.service';
import { AggregatedReportService } from '../shared/services/reporting-services/aggregated-report.service';
import { AnalyticalReportService } from '../shared/services/reporting-services/analytical-report.service';
import { EngagementReportService } from '../shared/services/reporting-services/engagement-report.service';
import { EvolutionReportService } from '../shared/services/reporting-services/evolution-report.service';
import { MeterReportService } from '../shared/services/reporting-services/meter-report.service';
import { RankingReportService } from '../shared/services/reporting-services/ranking-report.service';
import { SummaryReportService } from '../shared/services/reporting-services/summary-report.service';
import { WordCloudReportService } from '../shared/services/reporting-services/word-cloud-report.service';
import { IEvolutionReport } from '../utils/interfaces/evolution-report';
import { IPoll } from '../utils/interfaces/poll';
import { IReportFilter } from '../utils/interfaces/report-filter';
import { ISummaryReport } from '../utils/interfaces/summary-report';
import { ReservationReportService } from '../shared/services/reporting-services/reservation-report.service';

export const getDataWindowResolver: ResolveFn<any> = () => {
  const w: any = window;
  return w.data;
};

export const getAggregatedDataResolver: ResolveFn<{
  aggregatedData: unknown;
  aggregatedByDevicesData: unknown;
  aggregatedByDevicesAndShiftData: unknown;
  initialManagementDiaryData: {
    dataEvolution: IEvolutionReport | null;
    dataSummary: ISummaryReport | null;
    dataRanking: unknown;
    wordcloud: CloudData[];
  };
  filter: IReportFilter;
  poll: IPoll;
}> = (route) => {
  const aggregatedReportService = inject(AggregatedReportService);
  const pollService = inject(PollService);
  const rankingReportService = inject(RankingReportService);
  const summaryReportService = inject(SummaryReportService);
  const evolutionReportService = inject(EvolutionReportService);
  const wordCloudReportService = inject(WordCloudReportService);
  const filter = buildFilterFromRoute(route);
  const managementDiaryEvolutionFilter = buildManagementDiaryEvolutionFilter(filter);

  return forkJoin({
    aggregatedData: aggregatedReportService.getAggregatedReport(filter).pipe(
      map((res) => res.data ?? null),
      catchError(() => of(null))
    ),
    aggregatedByDevicesData: aggregatedReportService.getAggregatedByDevices(filter).pipe(
      map((res) => res.data ?? null),
      catchError(() => of(null))
    ),
    aggregatedByDevicesAndShiftData: aggregatedReportService
      .getAggregatedByDevicesAndShifts(filter)
      .pipe(
        map((res) => res.data ?? null),
        catchError(() => of(null))
      ),
    initialManagementDiaryData: forkJoin({
      dataEvolution: evolutionReportService
        .getEvolutionByDaysReport(managementDiaryEvolutionFilter)
        .pipe(
          map((res) => res.data ?? null),
          catchError(() => of(null))
        ),
      dataSummary: summaryReportService
        .getSummaryReport(filter)
        .pipe(
          map((res) => res.data ?? null),
          catchError(() => of(null))
        ),
      dataRanking: rankingReportService
        .getRankingReport(filter)
        .pipe(
          map((res) => res.data ?? []),
          catchError(() => of([]))
        ),
      wordcloud: wordCloudReportService.getWordCloudReport(filter).pipe(
        map((res) => {
          const wordCloud = res.data?.wordCloud ?? [];
          wordCloud.sort((a, b) => b.weight - a.weight);
          return wordCloud;
        }),
        catchError(() => of([]))
      ),
    }),
    filter: of(filter),
    poll: pollService.getPollById(filter.pollId).pipe(map((res) => res.data)),
  });
};

export const getSummaryDataResolver: ResolveFn<{
  data: ISummaryReport;
  filter: IReportFilter;
}> = (route) => {
  const summaryReportService = inject(SummaryReportService);
  const filter = buildFilterFromRoute(route);

  return forkJoin({
    data: summaryReportService
      .getSummaryReport(filter)
      .pipe(map((res) => res.data)),
    filter: of(filter),
  });
};

export const getManagementDiaryDataResolver: ResolveFn<{
  dataEvolution: IEvolutionReport;
  dataSummary: ISummaryReport;
  filter: IReportFilter;
  wordcloud: CloudData[];
}> = (route) => {
  const rankingReportService = inject(RankingReportService);
  const summaryReportService = inject(SummaryReportService);
  const evolutionReportService = inject(EvolutionReportService);
  const wordCloudReportService = inject(WordCloudReportService);
  const filter = buildFilterFromRoute(route);

  return forkJoin({
    dataEvolution: evolutionReportService
      .getEvolutionByDaysReport(buildManagementDiaryEvolutionFilter(filter))
      .pipe(map((res) => res.data)),
    dataSummary: summaryReportService
      .getSummaryReport(filter)
      .pipe(map((res) => res.data)),
    filter: of(filter),
    dataRanking: rankingReportService
      .getRankingReport(filter)
      .pipe(map((res) => res.data)),
    wordcloud: wordCloudReportService.getWordCloudReport(filter).pipe(
      map((res) => {
        res.data.wordCloud.sort((a, b) => b.weight - a.weight);

        return res.data.wordCloud;
      })
    ),
  });
};

function buildManagementDiaryEvolutionFilter(
  filter: IReportFilter,
): IReportFilter {
  const startDate =
    new Date(new Date().setDate(new Date().getDate() - 12))
      .toISOString()
      .split('T')[0] + 'T00:00:00.000Z';
  const finishDate = new Date().toISOString().split('T')[0] + 'T00:00:00.000Z';

  return {
    ...filter,
    startDate,
    finishDate,
  };
}

export const getEngagementDataResolver: ResolveFn<any> = (route) => {
  const engagementReportService = inject(EngagementReportService);
  const filter = buildFilterFromRoute(route);

  return forkJoin({
    filter: of(filter),
    data: engagementReportService
      .getEngagementReport(filter)
      .pipe(map((res) => res.data)),
  });
};

export const getEvolutionDataResolver: ResolveFn<any> = (route) => {
  const evolutionReportService = inject(EvolutionReportService);
  const filter = buildFilterFromRoute(route);

  return forkJoin({
    filter: of(filter),
    data: evolutionReportService
      .getEvolutionReport(filter)
      .pipe(map((res) => res.data)),
  });
};

export const getAnalyticalDataResolver: ResolveFn<any> = (route) => {
  const analyticalReportService = inject(AnalyticalReportService);
  const filter = buildFilterFromRoute(route);

  return forkJoin({
    filter: of(filter),
    data: analyticalReportService
      .getAnalyticalReport(filter)
      .pipe(map((res) => res.data)),
  });
};

export const getWordcloudDataResolver: ResolveFn<any> = (route) => {
  const wordCloudReportService = inject(WordCloudReportService);
  const pollService = inject(PollService);
  const filter = buildFilterFromRoute(route);

  return forkJoin({
    filter: of(filter),
    title: pollService
      .getPollById(filter.pollId)
      .pipe(
        map(
          (res) =>
            res.data.pages
              .find((p) => p.pageNumber === 1)
              ?.questions.find((q) => q.number === 1)?.title
        )
      ),
    data: wordCloudReportService.getWordCloudReport(filter).pipe(
      map((res) => {
        res.data.wordCloud.sort((a, b) => b.weight - a.weight);

        return res.data.wordCloud;
      })
    ),
  });
};

export const getMeterDataResolver: ResolveFn<any> = (route) => {
  const evolutionReportService = inject(EvolutionReportService);
  const meterReportService = inject(MeterReportService);
  const filter = buildFilterFromRoute(route);
  const finishDate = filter.finishDate;

  const initialDate =
    new Date(new Date(finishDate).setDate(new Date(finishDate).getDate() - 335))
      .toISOString()
      .split('T')[0] + 'T00:00:00.000Z';

  return forkJoin({
    filter: of(filter),
    dataEvolution: evolutionReportService
      .getEvolutionReport({ ...filter, startDate: initialDate })
      .pipe(map((res) => res.data[0])),
    dataMeter: meterReportService
      .getMeterReport(filter)
      .pipe(map((res) => res.data)),
  });
};

export const getReservationDataResolver: ResolveFn<any> = (route) => {
  const evolutionReportService = inject(ReservationReportService);
  const filter = buildFilterFromRoute(route);

  return forkJoin({
    filter: of(filter),
    data: evolutionReportService
      .getReservationReport(filter)
      .pipe(map((res) => res.data)),
  });
};

export function parseListParam(param?: string): string[] {
  if (!param) return [];
  return param.split(',').map(item => item.trim()).filter(Boolean);
}

export function buildFilterFromRoute(route: {
  params: Record<string, string>;
  queryParams: Record<string, string | undefined>;
}): IReportFilter {
  const pollId = route.params['pollId'];
  const {
    branchesId,
    startDate,
    finishDate,
    branchesName,
    avaiableShifts,
    devicesId,
    devicesName,
    answers,
    startTime,
    finishTime,
    profileId,
    userId,
    profileName,
    userName,
    weekDayName,
    responseDevice,
    responseType,
    branchOrUser,
    weekDay,
  } = route.queryParams;

  return {
    ...newFilter,
    pollId,
    branchesId: parseListParam(branchesId),
    branchesName: parseListParam(branchesName),
    avaiableShifts: parseListParam(avaiableShifts),
    devicesId: parseListParam(devicesId),
    devicesName: parseListParam(devicesName),
    answers: parseListParam(answers),
    startDate: startDate ?? '',
    finishDate: finishDate ?? '',
    startTime: startTime ?? '',
    finishTime: finishTime ?? '',
    profileId: toNumberOrNull(profileId),
    userId: userId ?? null,
    profileName,
    userName,
    weekDayName,
    responseDevice: toNumber(responseDevice),
    responseType: toNumber(responseType),
    branchOrUser: toNumber(branchOrUser),
    weekDay: toNumber(weekDay),
  };
}

function toNumber(value?: string): IReportFilter['responseDevice'] {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed >= 0 && parsed <= 7
    ? (parsed as IReportFilter['responseDevice'])
    : 0;
}

function toNumberOrNull(value?: string): IReportFilter['profileId'] {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed >= 0 && parsed <= 7
    ? (parsed as IReportFilter['profileId'])
    : null;
}

const newFilter: IReportFilter = {
  pollId: '',
  responseDevice: 0,
  responseType: 0,
  branchOrUser: 0,
  profileId: null,
  branchesId: [],
  branchesName: [],
  userId: null,
  weekDay: 0,
  startDate: '',
  finishDate: '',
  avaiableShifts: [],
  answers: [],
  startTime: '',
  finishTime: '',
};
